import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Blob } from './Blob';
import { businessData } from './seoConfig';
import { Images } from '../utils/cloudflareImages';
import { BackgroundVideo } from './BackgroundVideo';

// Cloudflare Stream Video IDs
const VIDEO_PLAYLIST = [
  'be5f900b212a46d7a1889acbfe41f8ae',
  'd1a4c92907b0421d9b713c0e85c39a3e',
  '4b12f0f6025648889a7f81408673f811',
  '57492b4024bd4c7f9e0cf266925e10fe',
  'ff5119d31878416fad33a0ec4114e68f',
  '467ef9ea18ef4abbac73a6d12c069ad8', // From Gallery
];

export const Hero: React.FC = () => {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [nextVideoIndex, setNextVideoIndex] = useState(1);
  const [showCurrent, setShowCurrent] = useState(true);

  // Function to go to next video with crossfade
  const goToNextVideo = () => {
    setShowCurrent(false);
    setTimeout(() => {
      setCurrentVideoIndex(nextVideoIndex);
      setNextVideoIndex((nextVideoIndex + 1) % VIDEO_PLAYLIST.length);
      setShowCurrent(true);
    }, 600); // Match the animation duration
  };

  // Auto-advance every 20 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      goToNextVideo();
    }, 20000); // 20 seconds per video

    return () => clearTimeout(timer);
  }, [currentVideoIndex, nextVideoIndex]);

  const currentVideoId = VIDEO_PLAYLIST[currentVideoIndex];
  const nextVideoId = VIDEO_PLAYLIST[nextVideoIndex];

  // Manual navigation
  const goToVideo = (index: number) => {
    if (index === currentVideoIndex) return;
    setShowCurrent(false);
    setTimeout(() => {
      setCurrentVideoIndex(index);
      setNextVideoIndex((index + 1) % VIDEO_PLAYLIST.length);
      setShowCurrent(true);
    }, 600);
  };

  return (
    <header className="relative overflow-visible py-32 px-6 md:px-20 bg-[#FAF8F4]">
      <Blob
        className="-z-10 left-8 top-12 w-80 h-80 md:w-96 md:h-96"
        style={{ opacity: 0.06 }}
      />

      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <motion.h1
              className="font-serif text-6xl md:text-7xl leading-tight text-[#0B0B0B]"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1, ease: 'easeOut' }}
            >
              GiGi Hair Braiding
            </motion.h1>
            <motion.p
              className="text-xl text-[#6B6970] leading-relaxed max-w-xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
            >
              Clean, considered braiding with luminous finishes. Retro elegance
              meets modern care.
            </motion.p>
            <motion.div
              className="flex gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: 'easeOut' }}
            >
              <Link
                to="/contact"
                className="px-8 py-4 bg-[#0B0B0B] text-white rounded-2xl font-medium shadow-sm hover:shadow-md transition-all"
              >
                Book Appointment
              </Link>
              <Link
                to="/services"
                className="px-8 py-4 border-2 border-[#0B0B0B] text-[#0B0B0B] rounded-2xl font-medium hover:bg-[#0B0B0B] hover:text-white transition-all"
              >
                View Services
              </Link>
            </motion.div>
          </motion.div>

          <motion.div
            className="pt-6 relative"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
          >
            <div className="border-2 border-[#0B0B0B] p-6 rounded-2xl">
              <div className="bg-gray-100 rounded-md overflow-hidden relative" style={{ aspectRatio: '9/16' }}>
                {/* Current Video Layer */}
                <motion.div
                  className="absolute inset-0 z-10"
                  initial={{ opacity: 1 }}
                  animate={{ opacity: showCurrent ? 1 : 0 }}
                  transition={{ duration: 0.6, ease: 'easeInOut' }}
                >
                  <iframe
                    key={`current-${currentVideoId}`}
                    src={`https://customer-egd8wletnhjj8i2u.cloudflarestream.com/${currentVideoId}/iframe?autoplay=true&muted=true&preload=auto&controls=false`}
                    className="absolute inset-0 w-full h-full"
                    style={{
                      border: 'none',
                    }}
                    allow="autoplay; encrypted-media;"
                    title="GiGi Salon - Professional Braiding"
                  />
                </motion.div>

                {/* Next Video Layer (Preloaded for smooth transition) */}
                <motion.div
                  className="absolute inset-0 z-0"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: showCurrent ? 0 : 1 }}
                  transition={{ duration: 0.6, ease: 'easeInOut' }}
                >
                  <iframe
                    key={`next-${nextVideoId}`}
                    src={`https://customer-egd8wletnhjj8i2u.cloudflarestream.com/${nextVideoId}/iframe?autoplay=true&muted=true&preload=auto&controls=false`}
                    className="absolute inset-0 w-full h-full"
                    style={{
                      border: 'none',
                    }}
                    allow="autoplay; encrypted-media;"
                    title="GiGi Salon - Next Video"
                  />
                </motion.div>
              </div>

              {/* Video Playlist Indicators */}
              <div className="flex justify-center gap-2 mt-4">
                {VIDEO_PLAYLIST.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToVideo(index)}
                    className={`h-2 rounded-full transition-all ${
                      index === currentVideoIndex
                        ? 'w-8 bg-[#0B0B0B]'
                        : 'w-2 bg-[#0B0B0B]/30 hover:bg-[#0B0B0B]/50'
                    }`}
                    aria-label={`Go to video ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </header>
  );
};

export default Hero;
